import {HStack} from 'platform/foundation-tw';
import {cva, type VariantProps} from 'platform/shared-tw';

import {createContext, useContext, useId, useState, useRef, useEffect} from 'react';

import {getSafely} from 'shared/web';

// ==========================================
// CONTEXT
// ==========================================

interface TabsContextValue {
  readonly activeValue: string;
  readonly onValueChange: (value: string) => void;
  readonly baseId: string;
}

const TabsContext = createContext<TabsContextValue | null>(null);

const useTabsContext = () => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('Tabs components must be used within a Tabs provider');
  }
  return context;
};

// ==========================================
// TABS (ROOT CONTAINER)
// ==========================================

export interface TabsProps {
  readonly defaultValue: string;
  readonly onValueChange?: (value: string) => void;
  readonly children?: React.ReactNode;
}

/**
 * TABS
 * Root container for tab navigation system
 *
 * @prop defaultValue - Initial active tab value
 * @prop onValueChange - Callback when active tab changes
 * @prop children - TabsList and TabsContent components
 */
export function Tabs(props: TabsProps) {
  const [activeValue, setActiveValue] = useState(props.defaultValue);
  const baseId = useId();

  const handleValueChange = (value: string) => {
    setActiveValue(value);
    props.onValueChange?.(value);
  };

  return (
    <TabsContext.Provider value={{activeValue, onValueChange: handleValueChange, baseId}}>
      <div>{props.children}</div>
    </TabsContext.Provider>
  );
}

// ==========================================
// TABS LIST
// ==========================================

export interface TabsListProps {
  readonly children?: React.ReactNode;
}

/**
 * TABSLIST
 * Container for tab triggers with keyboard navigation
 *
 * @prop children - TabsTrigger components
 */
export function TabsList(props: TabsListProps) {
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const activeTab = listRef.current?.querySelector(
      '[role="tab"][aria-selected="true"]'
    ) as HTMLElement;
    if (activeTab && document.activeElement === document.body) {
      // If no element is focused, we can optionally focus the active tab on mount
      // Commenting this out to not auto-focus on mount, but keep for reference
      // activeTab.focus();
    }
    return () => {
      // Cleanup not needed for this effect
    };
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const triggers = listRef.current?.querySelectorAll('[role="tab"]:not([aria-disabled="true"])');
    if (!triggers || triggers.length === 0) {
      return;
    }

    const triggerArray = Array.from(triggers) as HTMLElement[];
    const currentIndex = triggerArray.indexOf(document.activeElement as HTMLElement);

    if (currentIndex === -1) {
      // If no tab is focused yet, focus the first enabled tab
      if (
        event.key === 'ArrowLeft' ||
        event.key === 'ArrowRight' ||
        event.key === 'Home' ||
        event.key === 'End'
      ) {
        event.preventDefault();
        triggerArray[0]?.focus();
        triggerArray[0]?.click();
        return;
      }
      return;
    }

    let nextIndex = currentIndex;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      nextIndex = currentIndex <= 0 ? triggerArray.length - 1 : currentIndex - 1;
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      nextIndex = currentIndex >= triggerArray.length - 1 ? 0 : currentIndex + 1;
    } else if (event.key === 'Home') {
      event.preventDefault();
      nextIndex = 0;
    } else if (event.key === 'End') {
      event.preventDefault();
      nextIndex = triggerArray.length - 1;
    } else {
      return;
    }

    const nextTrigger = getSafely(triggerArray, nextIndex);
    nextTrigger?.focus();
    nextTrigger?.click();
  };

  // Note: Outer div required for ref, role, and onKeyDown accessibility props
  // which HStack does not support
  return (
    <div ref={listRef} role="tablist" onKeyDown={handleKeyDown}>
      <HStack spacing={8} align="center">
        {props.children}
      </HStack>
    </div>
  );
}

// ==========================================
// TABS TRIGGER
// ==========================================

const triggerVariants = cva(
  'inline-flex items-center gap-2 pb-5 border-b border-b-transparent font-[Geist] text-base leading-6 transition-colors cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-(--color-accent)',
  {
    variants: {
      isActive: {
        true: 'border-b-4 border-b-(--color-mira-gray-100) text-(--color-text-primary) font-semibold',
        false: 'text-(--color-text-secondary) hover:text-(--color-text-primary)',
      },
      isDisabled: {
        true: 'opacity-50 cursor-not-allowed pointer-events-none',
        false: '',
      },
    },
  }
);

export interface TabsTriggerProps extends VariantProps<typeof triggerVariants> {
  readonly value: string;
  readonly disabled?: boolean;
  readonly indicatorColor?: string;
  readonly children?: React.ReactNode;
}

/**
 * TABSTRIGGER
 * Individual tab button with active state, disabled state, and optional status indicator
 *
 * @prop value - Unique identifier for this tab
 * @prop disabled - Whether the tab is disabled
 * @prop indicatorColor - Optional color for status indicator dot (e.g., "var(--color-mira-red-50)")
 * @prop children - Tab label content
 */
export function TabsTrigger(props: TabsTriggerProps) {
  const context = useTabsContext();
  const isActive = context.activeValue === props.value;
  const isDisabled = props.disabled ?? false;

  const handleClick = () => {
    if (!isDisabled) {
      context.onValueChange(props.value);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!isDisabled) {
        context.onValueChange(props.value);
      }
    }
  };

  const tabId = `${context.baseId}-tab-${props.value}`;
  const panelId = `${context.baseId}-panel-${props.value}`;

  return (
    <button
      id={tabId}
      role="tab"
      type="button"
      aria-selected={isActive}
      aria-controls={panelId}
      aria-disabled={isDisabled}
      tabIndex={isDisabled ? -1 : 0}
      disabled={isDisabled}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={triggerVariants({isActive, isDisabled})}
    >
      <span>{props.children}</span>
      {props.indicatorColor && (
        <span
          className="size-2 rounded-full shrink-0"
          style={{backgroundColor: props.indicatorColor} as React.CSSProperties}
          aria-hidden="true"
        />
      )}
    </button>
  );
}

// ==========================================
// TABS CONTENT
// ==========================================

export interface TabsContentProps {
  readonly value: string;
  readonly children?: React.ReactNode;
}

/**
 * TABSCONTENT
 * Panel that displays content for the active tab
 *
 * @prop value - Unique identifier matching a TabsTrigger value
 * @prop children - Panel content
 */
export function TabsContent(props: TabsContentProps) {
  const context = useTabsContext();
  const isActive = context.activeValue === props.value;

  const tabId = `${context.baseId}-tab-${props.value}`;
  const panelId = `${context.baseId}-panel-${props.value}`;

  if (!isActive) {
    return null;
  }

  return (
    <div id={panelId} role="tabpanel" aria-labelledby={tabId} tabIndex={0}>
      {props.children}
    </div>
  );
}
